import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../../../../components/sidebar";
import { useModalContext } from "../../../CustomModal/ModalProvider.jsx";
import { appStatus } from "../../../../store/modules/app_status/actions";
import { GUARDIAN_PANEL } from "../../../../utils/appNavigation";
import {
  MainContainer,
  BackgroundPattern,
  SideContainer,
  ContentWrapper,
  HeaderSection,
  Title,
  Subtitle,
  ButtonRow,
  ButtonContainerLeft,
  ButtonContainerRight,
  StyledButton,
} from "../../ListAreas/styles";
import {
  FormContainer,
  FormRow,
  Label,
  Input,
  CheckboxWrapper,
  Checkbox,
  ErrorText,
} from "../CreateArea/styles";
import {
  DetailSection,
  DetailRow,
  DetailLabel,
  DetailValue,
  ActionRow,
} from "./styles";
import { firesService } from "../../../../services/firesService";

const FirmsAreaDetail = () => {
  const { fireAreaId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const fromAreaDetails = location?.state?.fromAreaDetails;
  const collapsed = useSelector((state) => state.sidebar);
  const { showConfirm, showSuccess, showError } = useModalContext();

  const [area, setArea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    bbox_west: "",
    bbox_south: "",
    bbox_east: "",
    bbox_north: "",
    active: true,
  });

  useEffect(() => {
    dispatch(appStatus(GUARDIAN_PANEL));
  }, [dispatch]);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await firesService.getArea(fireAreaId);
        setArea(data);
        setFormData({
          name: data.name || "",
          bbox_west: String(data.bbox_west ?? ""),
          bbox_south: String(data.bbox_south ?? ""),
          bbox_east: String(data.bbox_east ?? ""),
          bbox_north: String(data.bbox_north ?? ""),
          active: !!data.active,
        });
      } catch (err) {
        console.error("Erro ao carregar área:", err);
        showError("Erro", "Não foi possível carregar a área.");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [fireAreaId]);

  const handleBack = () => {
    if (fromAreaDetails) navigate(`/area_details/${fromAreaDetails}`);
    else if (area?.guardian_area) {
      navigate(`/area_details/${area.guardian_area}`);
    } else navigate("/areas");
  };

  const handleRunScan = async () => {
    setScanning(true);
    try {
      await firesService.runAreaScan(fireAreaId, 1);
      showSuccess("Sucesso", "Scan executado com sucesso.");
    } catch (err) {
      showError("Erro", err?.response?.data?.error || err?.message || "Erro ao executar scan.");
    } finally {
      setScanning(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    const west = parseFloat(formData.bbox_west);
    const south = parseFloat(formData.bbox_south);
    const east = parseFloat(formData.bbox_east);
    const north = parseFloat(formData.bbox_north);
    if (isNaN(west) || isNaN(south) || isNaN(east) || isNaN(north)) {
      setError("Bbox deve conter números válidos.");
      return;
    }
    if (west >= east || south >= north) {
      setError("Bbox inválida: west < east e south < north.");
      return;
    }
    setSaving(true);
    try {
      await firesService.patchArea(fireAreaId, {
        name: formData.name.trim(),
        bbox_west: west,
        bbox_south: south,
        bbox_east: east,
        bbox_north: north,
        active: formData.active,
      });
      setArea((prev) => ({ ...prev, ...formData, bbox_str: `${west},${south},${east},${north}` }));
      setEditing(false);
      showSuccess("Sucesso", "Área atualizada.");
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const ok = await showConfirm("Excluir área?", "Esta ação não pode ser desfeita.");
    if (!ok) return;
    try {
      await firesService.deleteArea(fireAreaId);
      showSuccess("Sucesso", "Área excluída.");
      if (fromAreaDetails) navigate(`/area_details/${fromAreaDetails}`);
      else navigate("/areas");
    } catch (err) {
      showError("Erro", err?.response?.data?.detail || err?.message || "Erro ao excluir.");
    }
  };

  const kmlUrl = area?.kml_file_url || area?.kml_file || null;

  const renderContent = () => {
    if (loading || !area) return <p style={{ color: "var(--text-heading)" }}>Carregando...</p>;

    return (
      <ContentWrapper>
        <ButtonRow>
          <ButtonContainerLeft>
            <StyledButton onClick={handleBack}>Voltar</StyledButton>
          </ButtonContainerLeft>
          <ButtonContainerRight>
            {!editing ? (
              <>
                <StyledButton onClick={() => setEditing(true)}>Editar</StyledButton>
                <StyledButton secondary onClick={handleRunScan} disabled={scanning}>
                  {scanning ? "Executando..." : "Executar scan"}
                </StyledButton>
                <StyledButton
                  secondary
                  onClick={() => navigate(`/firms/areas/${fireAreaId}/history`)}
                >
                  Ver histórico
                </StyledButton>
                <StyledButton
                  onClick={handleDelete}
                  style={{ background: "color-mix(in srgb, var(--danger) 60%, transparent)", borderColor: "var(--danger)" }}
                >
                  Excluir
                </StyledButton>
              </>
            ) : (
              <>
                <StyledButton onClick={() => setEditing(false)}>Cancelar</StyledButton>
                <StyledButton onClick={handleSave} disabled={saving}>
                  {saving ? "Salvando..." : "Salvar"}
                </StyledButton>
              </>
            )}
          </ButtonContainerRight>
        </ButtonRow>

        <HeaderSection>
          <Title>🔥 {area.name}</Title>
          <Subtitle>Detalhes da área de monitoramento FIRMS</Subtitle>
        </HeaderSection>

        {editing ? (
          <FormContainer onSubmit={handleSave}>
            <FormRow>
              <Label>Nome *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </FormRow>
            <FormRow>
              <Label>Bbox Oeste</Label>
              <Input
                type="number"
                step="any"
                value={formData.bbox_west}
                onChange={(e) => setFormData({ ...formData, bbox_west: e.target.value })}
              />
            </FormRow>
            <FormRow>
              <Label>Bbox Sul</Label>
              <Input
                type="number"
                step="any"
                value={formData.bbox_south}
                onChange={(e) => setFormData({ ...formData, bbox_south: e.target.value })}
              />
            </FormRow>
            <FormRow>
              <Label>Bbox Leste</Label>
              <Input
                type="number"
                step="any"
                value={formData.bbox_east}
                onChange={(e) => setFormData({ ...formData, bbox_east: e.target.value })}
              />
            </FormRow>
            <FormRow>
              <Label>Bbox Norte</Label>
              <Input
                type="number"
                step="any"
                value={formData.bbox_north}
                onChange={(e) => setFormData({ ...formData, bbox_north: e.target.value })}
              />
            </FormRow>
            <CheckboxWrapper>
              <Checkbox
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
              <Label as="span" style={{ margin: 0, marginLeft: "0.5rem" }}>Ativa</Label>
            </CheckboxWrapper>
            {error && <ErrorText>{error}</ErrorText>}
          </FormContainer>
        ) : (
          <DetailSection>
            <DetailRow>
              <DetailLabel>Nome</DetailLabel>
              <DetailValue>{area.name}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Bbox</DetailLabel>
              <DetailValue>{area.bbox_str || `${area.bbox_west},${area.bbox_south},${area.bbox_east},${area.bbox_north}`}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Ativa</DetailLabel>
              <DetailValue>{area.active ? "Sim" : "Não"}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>KML</DetailLabel>
              <DetailValue>
                {kmlUrl ? (
                  <a href={kmlUrl} target="_blank" rel="noopener noreferrer">
                    Baixar arquivo KML
                  </a>
                ) : (
                  "Não cadastrado"
                )}
              </DetailValue>
            </DetailRow>
            {area.nasa_firms_alert_subscription_id != null && (
              <DetailRow>
                <DetailLabel>Assinatura NASA FIRMS</DetailLabel>
                <DetailValue>{area.nasa_firms_alert_subscription_id}</DetailValue>
              </DetailRow>
            )}
            {area.guardian_area && (
              <DetailRow>
                <DetailLabel>Área de Interesse</DetailLabel>
                <DetailValue>
                  <button
                    type="button"
                    onClick={() => navigate(`/area_details/${area.guardian_area}`)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--accent)",
                      cursor: "pointer",
                      padding: 0,
                      font: "inherit",
                      textDecoration: "underline",
                    }}
                  >
                    Abrir área vinculada
                  </button>
                </DetailValue>
              </DetailRow>
            )}
            {area.created_at && (
              <DetailRow>
                <DetailLabel>Criado em</DetailLabel>
                <DetailValue>{new Date(area.created_at).toLocaleString("pt-BR")}</DetailValue>
              </DetailRow>
            )}
          </DetailSection>
        )}
      </ContentWrapper>
    );
  };

  return (
    <MainContainer collapsed={collapsed}>
      <BackgroundPattern />
      <Sidebar />
      <SideContainer collapsed={collapsed}>
        {renderContent()}
      </SideContainer>
    </MainContainer>
  );
};

export default FirmsAreaDetail;
